import React from 'react';
import type { DashboardShopPlayersData } from '../../../types';
import MetricStrip from './MetricStrip';
import TabSection from './TabSection';
import ChartCard from './charts/ChartCard';
import DonutChart from './charts/DonutChart';
import PlayerActivityChart from './charts/PlayerActivityChart';
import StackedBarChart from './charts/StackedBarChart';
import { BLUE_RAMP, CHART_COLORS, formatUsdFull } from './charts/common';
import { isCompositionEmpty, isSeriesEmpty, toBucketSeries, toPlayerSeries } from './executiveModel';

interface ShopPlayersTabProps {
  data: DashboardShopPlayersData | undefined;
  isLoading: boolean;
  error: Error | null;
  onRetry: () => void;
}

// Two stacks: money arriving (online + counter) beside money leaving.
const CHANNEL_SERIES = [
  { dataKey: 'onlineDeposits', name: 'Online deposits', color: BLUE_RAMP[0], stackId: 'in' },
  { dataKey: 'counterDeposits', name: 'Counter cash in', color: BLUE_RAMP[3], stackId: 'in' },
  { dataKey: 'onlineWithdrawals', name: 'Online withdrawals', color: CHART_COLORS.out, stackId: 'out' },
  { dataKey: 'counterRedemptions', name: 'Counter cash out', color: '#fbbf24', stackId: 'out' },
];

// Opposing flows — separate stacks so the bars sit side by side and no
// meaningless deposits+withdrawals total is implied.
const FLOW_SERIES = [
  { dataKey: 'deposits', name: 'Deposits', color: BLUE_RAMP[1], stackId: 'in' },
  { dataKey: 'withdrawals', name: 'Withdrawals', color: CHART_COLORS.out, stackId: 'out' },
];

/** Shop-linked players — those attached to a shop, moving money online and at the counter. */
const ShopPlayersTab: React.FC<ShopPlayersTabProps> = ({ data, isLoading, error, onRetry }) => {
  const channels = toBucketSeries(data?.charts?.channels, [
    'onlineDeposits',
    'counterDeposits',
    'onlineWithdrawals',
    'counterRedemptions',
  ]);
  const flow = toBucketSeries(data?.charts?.flow, ['deposits', 'withdrawals']);
  const players = toPlayerSeries(data?.charts);
  const bucketNote = data?.charts?.granularity === 'hour' ? 'Hourly buckets' : 'Daily buckets';

  const channelMix = data
    ? [
        { name: 'Online deposits', value: Math.max(0, data.kpis.onlineDepositsNetUsd.value) },
        {
          name: 'Counter cash',
          value: Math.max(0, data.kpis.counterDepositsCashReceived.value),
        },
      ]
    : [];

  return (
    <TabSection isLoading={isLoading} error={error} onRetry={onRetry}>
      {data && (
        <div className="space-y-4">
          <MetricStrip
            title="Shop economics"
            columnsClassName="grid-cols-2 sm:grid-cols-4"
            items={[
              { metric: 'shopNetProfit', kpi: data.kpis.shopNetProfit, format: 'currency', emphasis: true },
              { metric: 'playerMoneyIn', kpi: data.kpis.playerMoneyIn, format: 'currency' },
              { metric: 'shopSpent', kpi: data.kpis.shopSpent, format: 'currency', invertDelta: true },
              { metric: 'tierFundingUsd', kpi: data.kpis.tierFundingUsd, format: 'currency', invertDelta: true },
            ]}
          />

          <MetricStrip
            title="Online flow"
            columnsClassName="grid-cols-2 sm:grid-cols-4"
            items={[
              { metric: 'onlineDepositsNetUsd', kpi: data.kpis.onlineDepositsNetUsd, format: 'currency' },
              { metric: 'onlineDepositsCount', kpi: data.kpis.onlineDepositsCount, format: 'number' },
              { metric: 'onlineWithdrawalsGrossUsd', kpi: data.kpis.onlineWithdrawalsGrossUsd, format: 'currency' },
              { metric: 'onlineWithdrawalsCount', kpi: data.kpis.onlineWithdrawalsCount, format: 'number' },
            ]}
          />

          <MetricStrip
            title="Counter flow & players"
            columnsClassName="grid-cols-2 sm:grid-cols-4 xl:grid-cols-7"
            items={[
              { metric: 'counterDepositsCashReceived', kpi: data.kpis.counterDepositsCashReceived, format: 'currency' },
              { metric: 'counterDepositsCredits', kpi: data.kpis.counterDepositsCredits, format: 'number' },
              { metric: 'counterRedemptionsCashPaid', kpi: data.kpis.counterRedemptionsCashPaid, format: 'currency', invertDelta: true },
              { metric: 'counterRedemptionsCredits', kpi: data.kpis.counterRedemptionsCredits, format: 'number' },
              { metric: 'totalPlayers', kpi: data.kpis.totalPlayers, format: 'number' },
              { metric: 'activePlayers', kpi: data.kpis.activePlayers, format: 'number' },
              { metric: 'newPlayers', kpi: data.kpis.newPlayers, format: 'number' },
            ]}
          />

          <div className="grid gap-3 lg:grid-cols-3">
            <ChartCard
              metric="shopChannelMix"
              subtitle={bucketNote}
              className="lg:col-span-2"
              isEmpty={isSeriesEmpty(channels, [
                'onlineDeposits',
                'counterDeposits',
                'onlineWithdrawals',
                'counterRedemptions',
              ])}
            >
              <StackedBarChart
                data={channels}
                series={CHANNEL_SERIES}
                xKey="label"
                labelFormatter={(v) => v}
              />
            </ChartCard>

            <ChartCard metric="shopMoneyInMix" isEmpty={isCompositionEmpty(channelMix)}>
              <DonutChart
                data={channelMix}
                centerLabel="Money in"
                centerValue={formatUsdFull(data.kpis.playerMoneyIn.value)}
              />
            </ChartCard>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <ChartCard
              metric="depositsVsWithdrawals"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(flow, ['deposits', 'withdrawals'])}
            >
              <StackedBarChart
                data={flow}
                series={FLOW_SERIES}
                xKey="label"
                labelFormatter={(v) => v}
              />
            </ChartCard>

            <ChartCard
              metric="playerActivity"
              subtitle={bucketNote}
              isEmpty={isSeriesEmpty(players, ['newPlayers', 'activePlayers'])}
              emptyMessage="No player activity in this period"
            >
              <PlayerActivityChart data={players} />
            </ChartCard>
          </div>
        </div>
      )}
    </TabSection>
  );
};

export default ShopPlayersTab;
